import { APP_DOMAIN, SHARE_CONFIG } from '../config';
import { Share } from '@capacitor/share';
import { Capacitor } from '@capacitor/core';

/**
 * Genera un slug URL-friendly desde un texto
 * Convierte caracteres acentuados y especiales a sus equivalentes ASCII
 */
export const generateSlug = (text) => {
  if (!text) return 'articulo';
  
  // Mapa de caracteres acentuados a sus equivalentes sin acento
  const accentMap = {
    'á': 'a', 'é': 'e', 'í': 'i', 'ó': 'o', 'ú': 'u',
    'Á': 'a', 'É': 'e', 'Í': 'i', 'Ó': 'o', 'Ú': 'u',
    'à': 'a', 'è': 'e', 'ì': 'i', 'ò': 'o', 'ù': 'u',
    'À': 'a', 'È': 'e', 'Ì': 'i', 'Ò': 'o', 'Ù': 'u',
    'ä': 'a', 'ë': 'e', 'ï': 'i', 'ö': 'o', 'ü': 'u',
    'Ä': 'a', 'Ë': 'e', 'Ï': 'i', 'Ö': 'o', 'Ü': 'u',
    'â': 'a', 'ê': 'e', 'î': 'i', 'ô': 'o', 'û': 'u',
    'Â': 'a', 'Ê': 'e', 'Î': 'i', 'Ô': 'o', 'Û': 'u',
    'ã': 'a', 'õ': 'o', 'ñ': 'n',
    'Ã': 'a', 'Õ': 'o', 'Ñ': 'n',
    'ç': 'c', 'Ç': 'c',
    'ý': 'y', 'ÿ': 'y', 'Ý': 'y',
    '¿': '', '¡': ''
  };
  
  return text
    .toLowerCase()
    // Reemplazar caracteres acentuados usando el mapa
    .split('')
    .map(char => accentMap[char] || char)
    .join('')
    // Reemplazar caracteres especiales con guiones
    .replace(/[^a-z0-9]+/g, '-')
    // Quitar guiones múltiples
    .replace(/-+/g, '-')
    // Quitar guiones al inicio/final
    .replace(/(^-|-$)/g, '')
    // Limitar longitud
    .substring(0, 60);
};

/**
 * Convierte nombre de sección a slug
 */
export const sectionToSlug = (sectionName) => {
  const sectionMap = {
    'Buenos días': 'buenos-dias',
    'Evangelio': 'evangelio',
    'Vamos al cole': 'vamos-al-cole',
    'Vocaciones': 'vocaciones',
    'Te interesa': 'te-interesa',
    'Santos Agustinos': 'santos-agustinos',
    'Formación Agustiniana': 'formacion-agustiniana',
    'Historia y personajes + Sabías que…': 'historia-y-sabias-que',
    'Liturgia': 'liturgia',
    'Oración': 'oracion'
  };
  
  return sectionMap[sectionName] || generateSlug(sectionName);
};

/**
 * Genera deep link completo para un artículo
 */
export const generateArticleDeepLink = (article, sectionTitle) => {
  const sectionSlug = sectionToSlug(sectionTitle);
  const articleSlug = article.slug || generateSlug(article.title);
  
  return `${APP_DOMAIN}/${sectionSlug}/${article.id}/${articleSlug}`;
};

/**
 * Comparte un artículo usando Capacitor Share API (nativo) o Web Share API
 */
export const shareArticle = async (article, sectionTitle) => {
  const deepLink = generateArticleDeepLink(article, sectionTitle);
  
  // Generar descripción priorizada: subtítulo > excerpt > summary > descripción por defecto
  let description = SHARE_CONFIG.defaultDescription;
  if (article.subtitle) {
    description = article.subtitle;
  } else if (article.excerpt) {
    description = article.excerpt;
  } else if (article.summary) {
    description = article.summary;
  } else if (article.description) {
    description = article.description;
  }
  
  // Limitar descripción a 200 caracteres para mejor visualización
  if (description && description.length > 200) {
    description = description.substring(0, 197) + '...';
  }
  
  try {
    // Si estamos en plataforma nativa (iOS/Android), usar Capacitor Share
    if (Capacitor.isNativePlatform()) {
      await Share.share({
        title: article.title,
        text: description,
        url: deepLink,
        dialogTitle: 'Compartir artículo'
      });
      
      console.log('✅ Artículo compartido exitosamente (Capacitor)');
      return { success: true, method: 'native' };
    }
    
    // Si estamos en web/PWA, usar Web Share API
    const shareData = {
      title: article.title,
      text: description,
      url: deepLink
    };
    
    if (navigator.share && navigator.canShare && navigator.canShare(shareData)) {
      await navigator.share(shareData);
      console.log('✅ Artículo compartido exitosamente (Web Share API)');
      return { success: true, method: 'native' };
    } 
    
    // Fallback: copiar al portapapeles
    if (navigator.clipboard) {
      await navigator.clipboard.writeText(deepLink);
      console.log('📋 Enlace copiado al portapapeles');
      return { success: true, method: 'clipboard', url: deepLink };
    }
    
    // Fallback antiguo
    const textArea = document.createElement('textarea');
    textArea.value = deepLink;
    textArea.style.position = 'fixed';
    textArea.style.opacity = '0';
    document.body.appendChild(textArea);
    textArea.select();
    document.execCommand('copy');
    document.body.removeChild(textArea);
    return { success: true, method: 'legacy', url: deepLink };
  } catch (error) {
    // El usuario canceló o hubo un error
    // Capacitor Share puede lanzar errores con diferentes mensajes al cancelar
    const errorMessage = error.message || error.toString();
    const isCancelled = 
      error.name === 'AbortError' || 
      errorMessage.includes('cancel') ||
      errorMessage.includes('dismiss') ||
      errorMessage.includes('User cancelled');
    
    if (isCancelled) {
      console.log('ℹ️ Usuario canceló compartir');
      return { success: false, cancelled: true };
    }
    
    console.error('❌ Error al compartir:', error);
    return { success: false, error: error.message };
  }
};

/**
 * Comparte una sección completa
 */
export const shareSection = async (sectionTitle) => {
  const sectionSlug = sectionToSlug(sectionTitle);
  const deepLink = `${APP_DOMAIN}/seccion/${sectionSlug}`;
  
  try {
    // Si estamos en plataforma nativa (iOS/Android), usar Capacitor Share
    if (Capacitor.isNativePlatform()) {
      await Share.share({
        title: `${sectionTitle} - ${SHARE_CONFIG.siteName}`,
        text: `Descubre ${sectionTitle} en ${SHARE_CONFIG.siteName}`,
        url: deepLink,
        dialogTitle: 'Compartir sección'
      });
      return { success: true, method: 'native' };
    }
    
    // Si estamos en web/PWA, usar Web Share API
    const shareData = {
      title: `${sectionTitle} - ${SHARE_CONFIG.siteName}`,
      text: `Descubre ${sectionTitle} en ${SHARE_CONFIG.siteName}`,
      url: deepLink
    };

    if (navigator.share) {
      await navigator.share(shareData);
      return { success: true, method: 'native' };
    } else if (navigator.clipboard) {
      await navigator.clipboard.writeText(deepLink);
      return { success: true, method: 'clipboard', url: deepLink };
    }
  } catch (error) {
    const errorMessage = error.message || error.toString();
    const isCancelled = 
      error.name === 'AbortError' || 
      errorMessage.includes('cancel') ||
      errorMessage.includes('dismiss') ||
      errorMessage.includes('User cancelled');
    
    if (isCancelled) {
      return { success: false, cancelled: true };
    }
    return { success: false, error: error.message };
  }
};

/**
 * Actualiza meta tags dinámicos para mejor preview al compartir
 */
export const updateMetaTags = (article, sectionTitle) => {
  // Generar descripción priorizada: subtítulo > excerpt > summary > descripción por defecto
  let description = SHARE_CONFIG.defaultDescription;
  if (article.subtitle) {
    description = article.subtitle;
  } else if (article.excerpt) {
    description = article.excerpt;
  } else if (article.summary) {
    description = article.summary;
  } else if (article.description) {
    description = article.description;
  }
  
  // Limitar descripción a 200 caracteres
  if (description && description.length > 200) {
    description = description.substring(0, 197) + '...';
  }
  
  // Determinar la mejor imagen disponible
  const imageUrl = article.imageUrl || article.image || `${APP_DOMAIN}${SHARE_CONFIG.defaultImage}`;
  
  // Asegurar que la imagen tenga URL absoluta
  const absoluteImageUrl = imageUrl.startsWith('http') 
    ? imageUrl 
    : `${APP_DOMAIN}${imageUrl}`;
  
  // Open Graph
  updateMetaTag('og:type', 'article');
  updateMetaTag('og:site_name', SHARE_CONFIG.siteName);
  updateMetaTag('og:title', article.title);
  updateMetaTag('og:description', description);
  updateMetaTag('og:url', generateArticleDeepLink(article, sectionTitle));
  updateMetaTag('og:image', absoluteImageUrl);
  updateMetaTag('og:image:width', '1200');
  updateMetaTag('og:image:height', '630');
  
  // Twitter Card
  updateMetaTag('twitter:card', 'summary_large_image');
  updateMetaTag('twitter:site', '@agustinosOAR');
  updateMetaTag('twitter:title', article.title);
  updateMetaTag('twitter:description', description);
  updateMetaTag('twitter:image', absoluteImageUrl);
  
  // Meta description estándar
  updateMetaTag('description', description);
  
  // Actualizar el título de la página también
  document.title = `${article.title} | ${SHARE_CONFIG.siteName}`;
};

/**
 * Actualiza o crea un meta tag
 */
const updateMetaTag = (property, content) => {
  if (!content) return;
  
  // Intentar con property (Open Graph)
  let meta = document.querySelector(`meta[property="${property}"]`);
  
  // Si no existe, intentar con name (Twitter, description)
  if (!meta) {
    meta = document.querySelector(`meta[name="${property}"]`);
  }
  
  // Si existe, actualizar
  if (meta) {
    meta.setAttribute('content', content);
  } 
  // Si no existe, crear
  else {
    meta = document.createElement('meta');
    if (property.startsWith('og:') || property.startsWith('twitter:')) {
      meta.setAttribute(property.startsWith('og:') ? 'property' : 'name', property);
    } else {
      meta.setAttribute('name', property);
    }
    meta.setAttribute('content', content);
    document.head.appendChild(meta);
  }
};

